import { formatLocalDate } from "../../../../shared/local-date";
import type { ReadSourceType } from "../../../../shared/chat-sources";

export interface ReadRowSource {
  sourceId: string;
  sourceType: ReadSourceType;
  deleted?: boolean;
  date: string | null;
  clientName?: string | null;
  sessionType?: "initial" | "follow_up" | null;
  formType?: string | null;
  tag?: "win" | "obstacle" | "risk" | "na" | null;
  title?: string | null;
}

/** The bold part of a read row: "Jun 18 scorecard". */
export function readRowLabel(source: ReadRowSource): string {
  if (source.deleted) {
    const type =
      source.sourceType === "meeting_note" ? "meeting note" : source.sourceType;

    return `Deleted ${type}`;
  }

  const date = formatLocalDate(source.date) || "Undated";
  if (source.sourceType === "session") return `${date} scorecard`;

  if (source.sourceType === "form") {
    return source.formType === "monthly_checkpoint"
      ? `${date} monthly checkpoint`
      : `Week of ${date} weekly form`;
  }

  if (source.sourceType === "observation")
    return (
      source.title?.trim() ||
      `${date} ${source.tag ?? ""} observation`.replace("  ", " ")
    );

  return source.title?.trim() || `${date} meeting note`;
}

/** The trailing detail: "— Mary S. (Follow-up)". */
export function readRowDetail(source: ReadRowSource): string {
  if (source.deleted || source.sourceType !== "session") return "";

  if (!source.clientName && !source.sessionType) return "";

  const type =
    source.sessionType === "initial"
      ? "Initial"
      : source.sessionType === "follow_up"
        ? "Follow-up"
        : null;
  const client = source.clientName ?? "Unknown";

  return type ? `— ${client} (${type})` : `— ${client}`;
}

/**
 * One quiet log row inside a ReadsBlock: "READ · Jun 18 scorecard — Mary S.
 * (Follow-up)". Citation rung 0 — clicking opens the source flyover.
 */
export function ReadRow({
  source,
  onOpen,
}: {
  source: ReadRowSource;
  onOpen: (source: ReadRowSource) => void;
}) {
  const label = readRowLabel(source);
  const detail = readRowDetail(source);

  return (
    <button
      type="button"
      onClick={() => onOpen(source)}
      disabled={source.deleted}
      aria-label={`Open ${label}${detail ? ` ${detail}` : ""}`}
      className="group inline-flex w-fit items-baseline gap-[7px] text-left text-body-sm text-muted-foreground disabled:cursor-default"
    >
      <span className="text-caption font-bold uppercase tracking-[0.05em] text-subtle">
        Read
      </span>
      <span>
        <strong className="font-semibold text-muted-strong group-enabled:group-hover:underline">
          {label}
        </strong>
        {detail ? <span> {detail}</span> : null}
      </span>
    </button>
  );
}

/**
 * The thread's read log per the comp: a left-ruled group of read rows under
 * an "AlwaysAI read N sources" label.
 */
export function ReadsBlock({
  sources,
  onOpen,
}: {
  sources: ReadRowSource[];
  onOpen: (source: ReadRowSource) => void;
}) {
  if (sources.length === 0) return null;

  return (
    <div
      role="log"
      aria-label="Sources read"
      className="flex flex-col gap-1 border-l-2 border-border pl-3"
    >
      <p className="mb-[2px] text-xs font-semibold text-subtle">
        AlwaysAI read {sources.length} source{sources.length === 1 ? "" : "s"}
      </p>
      {sources.map((source) => (
        <ReadRow
          key={`${source.sourceType}:${source.sourceId}`}
          source={source}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}
